// src/Redux/slices/subscriptionSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import StorageFactory from "../../Util/StorageFactory";
import {
  SESSION_USER_ROLES_PERMISSIONS,
  SESSION_USER_SUBSCRIPTION,
} from "../../Util/VMConstants";
import { isEmpty } from "../../Util/UtilHelper";
import {
  getUserNotFoundFromApiBody,
  getUserNotFoundFromAxiosError,
} from "../../Util/authSessionHelper";
import { getUserSubscriptionStatusTier, parseSubscriptionFetchArg, clearSubscriptionStatusTierCache } from "../../Services/subscriptionTierService";

const sessionFactory = new StorageFactory("session");

export const fetchSubscriptionStatus = createAsyncThunk(
  "subscription/fetchSubscriptionStatus",
  async (arg, { rejectWithValue }) => {
    const { userID, forceRefresh } = parseSubscriptionFetchArg(arg);
    if (!userID) {
      return rejectWithValue("User ID is required");
    }

    try {
      const respData = await getUserSubscriptionStatusTier(userID, { forceRefresh });

      const userNotFound = getUserNotFoundFromApiBody(respData);
      if (userNotFound) {
        return rejectWithValue(userNotFound);
      }

      if (respData.status_code === 200) {
        const user = respData.user || {};

        // 🔹 USER FIELDS (ADDED)
        const userInfo = {
          id: user?.user_id || null,
          name: user?.name || "",
          email: user?.email || "",
          mobileNo: user?.mobile_no || "",
          userType: user?.user_type || "",
          profilePic: user?.image_url || "",
        };

        // 🔹 PERMISSIONS (UNCHANGED LOGIC)
        const permissions = !isEmpty(user?.permissions)
          ? user.permissions.map((p) => p.permission_id)
          : [];

        // 🔹 SESSION PERSISTENCE (UNCHANGED)
        sessionFactory.add(SESSION_USER_ROLES_PERMISSIONS, permissions);
        sessionFactory.add(SESSION_USER_SUBSCRIPTION, respData.plan);

        return {
          status: respData,      // existing
          permissions,           // existing
          user: userInfo,        // ✅ NEW
        };
      }

      return rejectWithValue(
        respData.message || "Failed to fetch subscription"
      );
    } catch (error) {
      const userNotFound = getUserNotFoundFromAxiosError(error);
      if (userNotFound) {
        return rejectWithValue(userNotFound);
      }
      return rejectWithValue(error.message || "Network error");
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: {
    status: null,        // existing
    permissions: [],     // existing
    user: {              // ✅ NEW
      id: null,
      name: "",
      email: "",
      mobileNo: "",
      userType: "",
      profilePic: "",
    },
    error: null,
    userNotFoundMessage: null,
  },
  reducers: {
    setUserNotFoundMessage(state, action) {
      state.userNotFoundMessage = action.payload || "User not found";
    },
    clearUserNotFoundMessage(state) {
      state.userNotFoundMessage = null;
    },
    clearSubscription(state) {
      clearSubscriptionStatusTierCache();
      state.status = null;
      state.permissions = [];
      state.user = {
        id: null,
        name: "",
        email: "",
        mobileNo: "",
        userType: "",
        profilePic: "",
      };
      state.error = null;
      state.userNotFoundMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
        state.status = action.payload.status;
        state.permissions = action.payload.permissions;
        state.user = action.payload.user;
        state.userNotFoundMessage = null;
      })
      .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
        state.error = action.payload;
        if (action.payload?.userNotFound) {
          state.userNotFoundMessage =
            action.payload.message || "User not found";
        }
      });
  },
});

export const {
  clearSubscription,
  setUserNotFoundMessage,
  clearUserNotFoundMessage,
} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;